import Guid from "_shared/guids/guid";
import Topic from "messaging/forums/topics/topic";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import ForumSchema, { ForumJSON, ForumParams } from "messaging/forums/_schemas/forum-schema";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import ForumName from "notifications/message-notifications/forum-info/forum-name";
import { ForumInfoJSON } from "notifications/message-notifications/forum-info/forum-info-schema";
import GuidSchema from "_shared/guids/guid-schema";
import ForumEntityClassSchema from "notifications/message-notifications/forum-info/forum-entity-class-schema";

export default class Forum {
  public static readonly EXAMPLE: Forum = new Forum({
    id: Guid.generate(),
    name: new ForumName("General Discussion"),
    topic: Topic.EXAMPLE,
    subscribers: [EntityMember.EXAMPLE],
    creator: EntityMember.EXAMPLE,
    createdDate: new AHDate(),
    numUnreadMessages: 5,
  });

  public static create(params: ForumParams): Forum {
    ForumSchema.validate(params);
    return new Forum(params);
  }

  public static fromJSON(json: ForumJSON): Forum {
    const parsed = ForumSchema.deserialize(json);
    return new Forum(parsed);
  }

  public static fromJSONArray(jsonArray: unknown): Forum[] | null | undefined {
    if (isNullOrUndefined(jsonArray)) return jsonArray;
    const parsedArray = ForumSchema.deserializeJSONArray(jsonArray);
    return parsedArray.map((parsed) => new Forum(parsed));
  }

  public readonly name: ForumName;
  public readonly topic?: Topic;
  public readonly subscribers?: EntityMember[];
  public readonly id?: Guid;
  public readonly creator?: EntityMember;
  public readonly createdDate?: AHDate;
  public readonly numUnreadMessages?: number;

  public constructor(params: ForumParams) {
    ForumSchema.validate(params);
    Object.assign(this, params);
    this.name = params.name;
  }

  public toJSON(): ForumJSON {
    return ForumSchema.serialize(this);
  }

  public toForumInfoJSON(): ForumInfoJSON {
    return {
      id: GuidSchema.validateJSON(this.id?.toJSON()),
      name: this.name.toJSON(),
      entityClass: ForumEntityClassSchema.validateJSON(this.topic?.entityClass?.toJSON()),
      entityId: this.topic?.entityId?.toJSON() ?? null,
    };
  }

  public isEqualTo(other: Forum): boolean {
    if (!other) return false;
    return this.id?.isEqualTo(other.id ?? null) ?? false;
  }

  public toString(): string {
    return `Forum { 
      id: ${this.id?.toString() ?? "undefined"}, 
      name: ${this.name.toString()} 
    }`;
  }
}

export class ForumDoesNotExistError extends Error {
  constructor(id?: Guid | string) {
    if (id instanceof Guid) {
      super(`Forum ${id} does not exist`);
    } else {
      super(`Forum does not exist: ${id}`);
    }
  }
}

export class UnauthorizedForumAccessException extends Error {
  constructor(forumId: Guid) {
    super(`User is not authorized to access forum ${forumId}`);
  }
}
